import { AxiosError } from 'axios';
import { useSnackbar } from 'notistack';
import { memo, useCallback, useState } from 'react';
import { GetBusyGuardReason } from 'src/components/dispatch/resolveBusyGuardReason';
import { ShipmentOrderLegRunForPlanEntity, SplitTargetPayload } from 'src/entities/ShipmentOrderLegRun.entity';
import { useMutationShipmentOrderLegRun } from 'src/hooks/useShipmentOrderLegRun';

import ShipmentOrderLegRunSplitForm from './index';

const JP_WEEKDAY = ['日', '月', '火', '水', '木', '金', '土'];

type Props = {
  open: boolean;
  legRun: ShipmentOrderLegRunForPlanEntity | null;
  invoiceNo?: string | null;
  storeName?: string | null;
  scheduledDate?: string | null;
  onClose: () => void;
  onSuccess?: () => void;
  /**
   * 自動配車の実行中に競合する書き込みを弾く（Task 10 #8）。未指定ならガードしない。
   * 「分割を確定」実行時点の最新状態を読ませるため、値ではなく getter で受ける。
   */
  getBusyGuardReason?: GetBusyGuardReason;
};

const ShipmentOrderLegRunSplitFormPresenter = memo(({
  open, legRun, invoiceNo, storeName, scheduledDate, onClose, onSuccess, getBusyGuardReason,
}: Props) => {
  const { enqueueSnackbar } = useSnackbar();
  const { splitLegRun } = useMutationShipmentOrderLegRun();
  const [loading, setLoading] = useState(false);

  const handleConfirm = useCallback((legRunId: number, splits: SplitTargetPayload[]) => {
    // Task 10 #8: 「分割を確定」の直前で busy を判定する。
    const busyReason = getBusyGuardReason?.();
    if (busyReason) {
      enqueueSnackbar(busyReason, { variant: 'error' });
      return;
    }
    setLoading(true);
    splitLegRun.mutate(
      { id: legRunId, splits },
      {
        onSuccess: () => {
          enqueueSnackbar('案件を分割しました', { variant: 'success' });
          onClose();
          if (onSuccess) onSuccess();
        },
        onError: (error: AxiosError<{ errors?: string[] }>) => {
          const messages = error.response?.data?.errors;
          if (messages && messages.length > 0) {
            messages.forEach((m) => enqueueSnackbar(m, { variant: 'error' }));
          } else {
            enqueueSnackbar('分割処理でエラーが発生しました', { variant: 'error' });
          }
        },
        onSettled: () => setLoading(false),
      },
    );
  }, [splitLegRun, enqueueSnackbar, onClose, onSuccess, getBusyGuardReason]);

  if (!legRun || legRun.id === undefined) return null;

  const resolvedInvoiceNo = invoiceNo !== undefined ? invoiceNo : (legRun.code ?? null);
  const resolvedStoreName = storeName !== undefined ? storeName : (legRun.unloading_name ?? null);
  const resolvedScheduledDate = (() => {
    if (scheduledDate !== undefined) return scheduledDate;
    if (!legRun.unloading_start_at) return null;
    const d = new Date(legRun.unloading_start_at);
    if (Number.isNaN(d.getTime())) return null;
    // サンプル準拠の和暦風表記「M月D日 (曜日)」
    return `${d.getMonth() + 1}月${d.getDate()}日 (${JP_WEEKDAY[d.getDay()]})`;
  })();

  return (
    <ShipmentOrderLegRunSplitForm
      open={open}
      legRunId={legRun.id}
      items={legRun.items ?? []}
      invoiceNo={resolvedInvoiceNo}
      storeName={resolvedStoreName}
      scheduledDate={resolvedScheduledDate}
      loading={loading}
      onClose={onClose}
      onConfirm={handleConfirm}
    />
  );
});

export default ShipmentOrderLegRunSplitFormPresenter;
